import { useQueryState } from "nuqs"
import { z } from "zod"
import { useForm } from "react-hook-form"
import { toast } from "sonner"
import { useEffect } from "react"
import { useRouter } from "next/navigation"

import { zodResolver } from "@hookform/resolvers/zod"
import { useSession } from "@/contexts/SessionContext"
import { submitConsultantReview } from "@/actions/user/user.action"
import { useModalStore } from "@/hooks/useModal"
import { Dialog, DialogContent } from "@/components/molecules/Dialog/content"
import { Form } from "@/contexts/FormContext"
import { DialogTitle } from "@/components/atoms/Modal/dialog-title"
import { FormField } from "@/components/molecules/Form/field"
import { FormItem } from "@/components/molecules/Form/item"
import { FormLabel } from "@/components/molecules/Form/label"
import { FormMessage } from "@/components/molecules/Form/message"
import { RatingField } from "@/components/molecules/Rating/field"
import { FormControl } from "@/components/molecules/Form/control"
import { Textarea } from "@/components/atoms/Textarea"
import { Button } from "@/components/atoms/Button/button"
import { SubmitButton } from "@/components/molecules/SubmitButton/submit-button"
import { DialogDescription } from "@/components/molecules/Dialog/description"
import { DialogFooter } from "@/components/molecules/Dialog/footer"
import { DialogHeader } from "@/components/molecules/Dialog/header"
import { submitProductReview } from "@/actions/product/product.action"
import useLocaleStore from "@/app/stores/useLocaleStore"
import { ClientProviders } from "@/app/providers"

const formSchema = z.object({
  rating: z.number().min(1, "Please provide a rating").max(5, "Max rating is 5"),
  review: z.string().min(3, { message: "Write something about your experience" }),
})

const ReviewForm = () => {
  const { modals, closeModal } = useModalStore()
  const [pid, setPid] = useQueryState("pid", { clearOnDefault: true })
  const [type, setType] = useQueryState("type", { clearOnDefault: true })
  const { session, loading } = useSession()
  const lang = useLocaleStore().locale
  const router = useRouter()

  const userId = session?.user?.id
  const userFullName = session?.user?.name

  useEffect(() => {
    // Wait until loading finishes to decide.
    if (loading) return;

    // If modal is open but user is not logged in
    if (!userId && modals.review) {
      toast.error("You must be logged in to leave a review.");
      closeModal("review");

      const callbackUrl = window.location.pathname + window.location.search;
      router.push(`/auth/login?callbackUrl=${encodeURIComponent(callbackUrl)}`);
    }
  }, [userId, loading, modals.review, closeModal, router]);

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      rating: 5,
      review: "",
    },
  })

  async function onSubmit(payload: z.infer<typeof formSchema>) {
    try {
      if (!pid || !userId) {
        toast.error("There was an error submitting your review. Please try again.")
        return
      }

      const action = type === "product" ? submitProductReview : submitConsultantReview

      const reviewData =
        type === "product"
          ? {
            ...payload,
            productId: pid,
            fullName: userFullName,
            userId: userId,
          }
          : {
            ...payload,
            userId: userId,
            consultantId: pid,
            userName: userFullName,
          }

      const response = await action(reviewData)

      if (response.success) {
        toast.success("Your review has been submitted successfully.")
        setPid(null)
        setType(null)
        closeModal("review")
        form.reset()
      } else {
        // Handle specific domain validation errors gracefully
        toast.error(response.message || "Could not submit review")
      }
    } catch (err) {
      console.error("Review submission error:", err)
      toast.error("There was an unexpected error submitting your review. Please try again.")
    }
  }

  return (
    <Dialog
      open={!!modals["review"]}
      onOpenChange={() => {
        setPid(null)
        closeModal("review")
      }}
    >
      <DialogContent className="bg-white p-4 dark:bg-gray-900 sm:max-w-[625px]">
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <DialogHeader className="border-b border-gray-500 pb-4">
              <DialogTitle>{lang === "am" ? "ሀሳቦን ያስቀምጡ" : "Leave a Review"}</DialogTitle>
              <DialogDescription>{lang === "am" ? "ሃሳብዎን እና ልምድዎን ያካፍሉ" : "Share your thoughts and experience."}</DialogDescription>
            </DialogHeader>

            <FormField
              name="rating"
              control={form.control}
              render={({ field }) => (
                <FormItem className="text-center">
                  <FormLabel>{lang === "am" ? "ደረጃ" : "Rating"}</FormLabel>
                  <FormControl>
                    <RatingField rating={field.value} setRating={field.onChange} />
                  </FormControl>
                  <FormMessage className="text-xs font-normal text-red-400" />
                </FormItem>
              )}
            />

            <FormField
              name="review"
              control={form.control}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{lang === "am" ? "የእርስዎ ግምገማ" : "Your review"}</FormLabel>
                  <FormControl>
                    <Textarea
                      autoFocus
                      className="max-h-96 text-base"
                      placeholder={lang === "am" ? "የእርስዎ ግምገማ..." : "Share your thoughts..."}
                      rows={4}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage className="text-xs font-normal text-red-400" />
                </FormItem>
              )}
            />

            <DialogFooter className="flex-row justify-end space-x-2 pt-4">
              <Button
                type="button"
                variant="secondary"
                onClick={() => {
                  setPid(null)
                  closeModal("review")
                }}
              >
                {lang === "am" ? "ሰርዝ" : "Cancel"}
              </Button>
              <SubmitButton
                type="submit"
                variant="default"
                className="flex items-center justify-center text-center"
                disabled={loading || form.formState.isSubmitting}
                text={lang === "am" ? "ሀሳቦን ያስገቡ" : "Submit Review"}
                loadingText={lang === "am" ? "በመቀመጥ ላይ..." : "Submitting..."}
              />
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}

export function ReviewModal() {
  return (
    <ClientProviders>
      <ReviewForm />
    </ClientProviders>
  )
}